import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { computedFn } from 'mobx-utils';
import Favico from 'favico.js';
import i18n from 'i18next';
import * as utils from '../../helpers/utils';
import notificationSoundFile from '../../assets/sounds/ring.mp3';
import socketIo from '../../socket/socketIo';
import { getChannelName } from '../../helpers/channelHelpers';
import {
  mapCustomerSearchResultsContacts,
  parseIncomingContacts,
} from '../../helpers/contactHelpers';
import { BrowserDetect } from '../../helpers/browserDetect';

class CommonStore {
  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  loginRetries = 0;

  ignoreSettingsEntries = 0;

  ignoreAutoReplyEntries = 0;

  ignoreAutoReplyV2Entries = 0;

  loginToken = null;

  clientLocationsFromCp = null;

  @observable locations = [];

  userPreviousLoginInfo = null;

  messagesContainerRef = null;

  leftSideBarListContainerRef = null;

  loggedUserId = null;

  oldScrollHeight = 0;

  specificScrollHeight = 0;

  AudioContextInstance = window.AudioContext || window.webkitAudioContext;

  @observable audioContext = null;

  notificationsAudioRef = new Audio(notificationSoundFile);

  notificationRef = null;

  @observable cpHeaderHeight = null;

  ignoreNotifications = false;

  clientWebsiteUrl = i18n.t('Common.YourWebSite');

  @observable notificationsTitle = '';

  @observable notificationsOptions = {};

  notificationsChannel = {};

  @observable username = '';

  @observable cpUserId = '';

  @observable password = '';

  @observable searchResultData = [];

  @observable ChatMode = '';

  favicon = new Favico({
    animation: 'none',
  });

  @observable loggedIn = false;

  @observable showLocationPicker = false;

  @observable selectedLocation = null;

  @observable authenticationLoading = false;

  @observable isSettingsMenuOpen = false;

  @observable lastSeenMessageForChannel;

  @observable componentToRender;

  @observable isDataLoaded = false;

  @observable locationHasMoreChannels = true;

  @observable isWaitingForLoadMoreChannels = false;

  @observable channelHasMoreMessages = false;

  @observable locationTrackingNumber;

  @observable maxVisibleMessages;

  @observable lastNotificationMessage;

  @observable dragAndDropChildElementCounter = 0;

  @observable selectedTab = 1;

  @observable isDisconnected = false;

  @observable reconnectAttemptCount = 0;

  @observable loadingChannelMessages = false;

  @observable currentlyFocusedTab = null;

  @observable modalTitle = '';

  @observable numberOfVisibleTabs = 1;

  @observable mainComponentOpen = null;

  @observable.ref rightSideComponentOpen = null;

  @observable isLoadingSearchResultData = false;

  @observable channelSearchInput = '';

  @observable channelInfoInnerComponent = null;

  @observable isUploading = false;

  @observable selectedCannedMessage = '';

  @observable fullModeChatInput = '';

  @observable fullModeChatFileUpload = null;

  @observable snoozeInterval = null;

  @observable popoverButton = null;

  @observable cannedMessagesListOpen = false;

  @observable transferLocationListOpen = false;

  @observable isFetchingSearchResults = false;

  @observable notificationsBlocked = true;

  @observable customModalTitle = '';

  @observable snackbarMessage = null;

  @observable contactTypeIdsToSearch = [];

  @observable channelTypeIdsToSearch = [];

  @observable availableModes = [];

  @observable contactInfoMatchedContacts = [];

  @observable ignoreLoadMoreMessages = false;

  @observable isWebchatEnabled = false;

  @observable isAutoCloseWebChatEnabled = false;

  @observable showActiveWebChatChannels = true;

  @observable showClosedWebChatChannels = false;

  @observable showUnreadChannelFilter = false;

  @observable displayEditCustomerNamesForm = false;

  @observable isManageAutoRepliesPopupOpen = false;

  @observable cannedMessagesKeywords = {
    '[FIRST_NAME]': i18n.t('Common.CannedMessageKeyWordCustomerFirstName'),
    '[COMPANY_NAME]': i18n.t('Common.CannedMessageKeyWordCompanyName'),
    '[COMPANY_PHONE]': i18n.t('Common.CannedMessageKeyWordCompanyPhone'),
  };

  @observable conflicts = '';

  @observable selectedMessageTypeId = null;

  @observable selectedAutoReplySwitch = null;

  @observable customersData = [];

  @observable chatInputStyleHeight = 0;

  @computed get getLoggedUser() {
    return this.rootStore.settingsStore.settings &&
      this.rootStore.settingsStore.settings.length !== 0 &&
      this.rootStore.settingsStore.getLoggedUserSettings
      ? this.rootStore.settingsStore.getLoggedUserSettings.User
      : null;
  }

  @computed get toolbarDropdownLocations() {
    return this.locations
      .map((data) => ({ value: data, label: data.LocationName }))
      .sort((a, b) => {
        if (a.value.hasUnseenMessages && !b.value.hasUnseenMessages) {
          return -1;
        }

        if (!a.value.hasUnseenMessages && b.value.hasUnseenMessages) {
          return 1;
        }

        if (a.value.CPLocationID < b.value.CPLocationID) {
          return -1;
        }

        if (a.value.CPLocationID > b.value.CPLocationID) {
          return 1;
        }

        return 0;
      });
  }

  @computed get getSearchResultsFilteredByType() {
    const customersSearchResults =
      this.computedGetCustomersSearchResults?.length > 0
        ? this.computedGetCustomersSearchResults
        : [];

    const otherSearchResults =
      this.searchResultData?.length > 0
        ? this.searchResultData.filter(
            (searchResultData) =>
              !customersSearchResults.some(
                (posListSearchResult) =>
                  searchResultData.contactType === posListSearchResult.contactType &&
                  searchResultData.number === posListSearchResult.number
              )
          )
        : [];

    const searchResultData = [...customersSearchResults, ...otherSearchResults];

    if (this.availableModes?.length > 1 && this.contactTypeIdsToSearch?.length <= 1) {
      return searchResultData.filter((c) =>
        this.contactTypeIdsToSearch.includes(c?.data?.ContactType?.TypeId)
      );
    }

    return searchResultData;
  }

  @computed get getTransferLocationsList() {
    return this.locations.filter(
      (location) =>
        location.CPLocationID !== this.selectedLocation.CPLocationID && location.WebsiteChatIsActive
    );
  }

  @computed get getHasAccessToTextChats() {
    return this.availableModes.some((s) => s === utils.availableModes.CustomerChat);
  }

  @computed get getHasAccessToWebChats() {
    return this.availableModes.some((s) => s === utils.availableModes.WebsiteChat);
  }

  @computed get getHasOnlyWebChatSearchResults() {
    return (
      this.getSearchResultsFilteredByType?.length > 0 &&
      utils.isValidPhoneNumber(this.channelSearchInput) &&
      !this.getSearchResultsFilteredByType.some(
        (c) => c?.data?.ContactType?.TypeId === utils.ContactTypeEnum.Customer.TypeId
      )
    );
  }

  sortNames = (a, b) => {
    const aFirstName = a.firstName?.toLowerCase();
    const bFirstName = b.firstName?.toLowerCase();
    const aLastName = a.lastName?.toLowerCase();
    const bLastName = b.lastName?.toLowerCase();
    const aCompanyName = a.companyName?.toLowerCase();
    const bCompanyName = b.companyName?.toLowerCase();

    return (
      aFirstName?.localeCompare(bFirstName) ||
      aLastName?.localeCompare(bLastName) ||
      aCompanyName?.localeCompare(bCompanyName)
    );
  };

  @computed get computedGetCustomersSearchResults() {
    if (!this.availableModes.some((s) => s === utils.availableModes.CustomerChat)) {
      return [];
    }

    if (!this.channelSearchInput?.trim()) {
      return [];
    }

    let results = [];

    let text = this.channelSearchInput;

    const sanitizedInput = text.replace(/[^A-Za-z0-9]/g, '');
    if (/^\d+$/.test(sanitizedInput)) {
      text = sanitizedInput;
    }

    const searchTerms = text && text.trim() ? text.trim().split(' ') : null;

    if (searchTerms) {
      if (searchTerms.length > 1) {
        results = this.customersData
          ?.filter(
            (contact) =>
              (contact.firstName?.toLowerCase().startsWith(searchTerms[0]?.toLowerCase()) &&
                contact.lastName?.toLowerCase().startsWith(searchTerms[1]?.toLowerCase())) ||
              contact.companyName?.toLowerCase().includes(text.trim().toLowerCase())
          )
          .sort((a, b) => this.sortNames(a, b));
      } else {
        results = this.customersData
          ?.filter(
            (contact) =>
              contact.firstName?.toLowerCase().startsWith(searchTerms[0]?.toLowerCase()) ||
              contact.lastName?.toLowerCase().startsWith(searchTerms[0]?.toLowerCase()) ||
              contact.companyName?.toLowerCase().includes(searchTerms[0]?.toLowerCase()) ||
              contact.phones?.some((phoneNumber) => phoneNumber.number?.includes(searchTerms[0]))
          )
          .sort((a, b) => this.sortNames(a, b));
      }
    }

    return parseIncomingContacts(
      mapCustomerSearchResultsContacts(
        results,
        this.rootStore.contactTypeStore.computedGetByTypeId(utils.ContactTypeEnum.Customer.TypeId)
      ),
      this.channelSearchInput,
      this.rootStore.settingsStore.getLoggedUserSettings
    );
  }

  @action setInitialData(incData) {
    if (this.ignoreSettingsEntries === 0) {
      this.rootStore.settingsStore.UpsertAndGet(incData.settings);
    } else {
      this.ignoreSettingsEntries -= 1;
    }

    this.loggedUserId = incData.loggedUser._id;
    this.setLocationTrackingNumber(incData.locationTrackingNumber);
    this.setLastSeenMessageForChannel(incData.lastSeenMessageForChannel);
    this.componentToRender = null;

    this.setChannelHasMoreMessages(
      incData.settings.SelectedChannel
        ? incData.messages.filter(
            (f) => f.Channel && f.Channel._id === incData.settings.SelectedChannel._id
          ).length >= utils.maxNumberOfItemsByContainerSize(document, this.messagesContainerRef)
        : false
    );

    this.rootStore.channelStore.UpsertAndGet(incData.channels);
    this.rootStore.channelTypeStore.UpsertAndGet(incData.channelTypes);
    this.rootStore.contactTypeStore.UpsertAndGet(incData.contactTypes);
    this.rootStore.messageTypeStore.UpsertAndGet(incData.messageTypes);
    this.rootStore.smsDeliveryStatusStore.UpsertAndGet(incData.smsDeliveryStatus);
    this.rootStore.autoReplyTypeStore.UpsertAndGet(incData.autoReplyTypes);
    this.rootStore.autoReplyStore.UpsertAndGet(incData.autoReplies);
    this.rootStore.autoReplyV2Store.UpsertAndGet(incData.autoRepliesV2);
    this.rootStore.messageStore.UpsertAndGet(incData.messages);
    this.rootStore.cannedMessageStore.UpsertAndGet(incData.cannedMessages);
    this.rootStore.featureFlagStore.UpsertAndGet(incData.featureFlags);
    this.rootStore.cannedMessageTypeStore.UpsertAndGet(incData.cannedMessageTypes);
    this.rootStore.workTimeStore.UpsertAndGet(incData.workTime);

    this.clientWebsiteUrl = incData.clientWebsiteUrl;
    this.availableModes = incData.availableModes;
    this.contactTypeIdsToSearch = [];
    this.channelTypeIdsToSearch = [];

    if (this.availableModes.some((s) => s === utils.availableModes.CustomerChat)) {
      this.contactTypeIdsToSearch.push(utils.ContactTypeEnum.Customer.TypeId);
      this.channelTypeIdsToSearch.push(utils.ChannelTypeEnum.SMS.TypeId);
    }

    if (this.availableModes.some((s) => s === utils.availableModes.WebsiteChat)) {
      this.contactTypeIdsToSearch.push(utils.ContactTypeEnum.WebChatContact.TypeId);
      this.channelTypeIdsToSearch.push(utils.ChannelTypeEnum.WebChat.TypeId);
    }

    this.setIsWebchatEnabled(incData.isWebchatEnabled);

    this.setIsAutoCloseWebChatEnabled(incData.isAutoCloseWebChatEnabled);

    this.setCustomersData(incData.customersData);

    this.setIsDataLoaded(true);

    if (!socketIo.otherLocations.length) {
      this.connectToOtherNamespaces();
    }
  }

  @action connectToOtherNamespaces() {
    this.setConnectToCommon();

    this.rootStore.receivingEvents.updateLocations(false, this.selectedLocation.CPLocationID);

    if (
      this.getLoggedUser.ContactType.TypeId !== utils.ContactTypeEnum.Support.TypeId ||
      (this.getLoggedUser.ContactType.TypeId === utils.ContactTypeEnum.Support.TypeId &&
        this.cpHeaderHeight)
    ) {
      this.setConnectToAvailableLocationsForNotifications();
    }
  }

  @action setConnectToLocationSocket() {
    if (socketIo?.locationSocket) {
      socketIo.locationSocket.close();
      socketIo.locationSocket.removeAllListeners();
      socketIo.locationSocket = null;
    }

    if (socketIo?.otherLocations?.length) {
      socketIo.otherLocations.forEach((f) => {
        f.close();
        f.removeAllListeners();
      });
      socketIo.clearOtherLocations();
    }

    // if (Object.keys(managers)[0]) {
    //   managers[Object.keys(managers)[0]].nsps = {};
    // }

    const query = {
      locationId: this.selectedLocation.CPLocationID.toString(),
      locationName: this.selectedLocation.LocationName.replace(/\W/g, ''),
      username: this.username,
      cpUserId: this.cpUserId,
      language: window.navigator.language || window.navigator.userLanguage,
    };

    socketIo.establishConnection(this.selectedLocation.CPLocationID, query);

    this.rootStore.receivingEvents.AttachSocketReceivingEvents();

    socketIo.locationSocket.connect();
  }

  @action setConnectToAvailableLocationsForNotifications() {
    this.locations
      .filter((f) => f.CPLocationID !== this.selectedLocation.CPLocationID)
      .forEach((location) => {
        const query = {
          locationId: location.CPLocationID.toString(),
          locationName: this.selectedLocation.LocationName.replace(/\W/g, ''),
          username: this.username,
          cpUserId: this.cpUserId,
          notificationsOnly: true,
          language: window.navigator.language || window.navigator.userLanguage,
        };

        const currentSocketConnection = socketIo.establishConnection(
          location.CPLocationID,
          query,
          true
        );

        this.rootStore.receivingEvents.AttachOtherLocationsSocketEvents(currentSocketConnection);
        currentSocketConnection.connect();
      });
  }

  @action setConnectToCommon() {
    const query = {
      locationId: this.selectedLocation.CPLocationID.toString(),
      locationName: this.selectedLocation.LocationName.replace(/\W/g, ''),
      username: this.username,
      cpUserId: this.cpUserId,
      language: window.navigator.language || window.navigator.userLanguage,
    };

    const currentSocketConnection = socketIo.establishConnection('common', query, true);

    this.rootStore.receivingEvents.AttachCommonSocketEvents(currentSocketConnection);

    currentSocketConnection.connect();
  }

  scrollToSelectedChannel() {
    const settings = this.rootStore.settingsStore.getLoggedUserSettings;

    if (settings && settings.SelectedChannel && window.innerWidth > 959) {
      const selectedChannel = document.getElementById(settings.SelectedChannel._id);
      if (selectedChannel) {
        selectedChannel.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }

  @action setLogoutStatus = (checkForLocation) => {
    if (socketIo?.loginSocket) {
      socketIo.loginSocket.close();
    }

    if (socketIo?.locationSocket) {
      socketIo.locationSocket.close();
    }

    if (socketIo?.otherLocations) {
      socketIo.otherLocations.forEach((f) => f.close());
      socketIo.clearOtherLocations();
    }

    this.favicon.badge(0);
    this.setIsDataLoaded(false);
    this.loggedIn = false;
    this.setAuthenticationLoading(false);
    this.isSettingsMenuOpen = false;
    this.notificationsTitle = '';
    this.notificationsOptions = {};
    this.mainComponentOpen = null;
    this.rightSideComponentOpen = null;
    this.setIsLoadingSearchResultData(false);
    this.setChannelSearchInput('');
    this.setFullModeChatFileUpload(null);
    this.loadingChannelMessages = false;
    this.isUploading = false;
    this.dragAndDropChildElementCounter = 0;
    this.fullModeChatInput = '';
    this.setIsWaitingForLoadMoreChannels(false);
    this.setLocationHasMoreChannels(true);

    this.loggedUserId = null;
    this.loginToken = null;
    this.searchResultData = [];
    this.showUnreadChannelFilter = false;

    this.rootStore.channelTypeStore.channelTypes.clear();
    this.rootStore.contactTypeStore.contactTypes.clear();
    this.rootStore.messageTypeStore.messageTypes.clear();
    this.rootStore.smsDeliveryStatusStore.smsDeliveryStatuses.clear();
    this.rootStore.pushNotificationTokenStore.pushNotificationTokens.clear();
    this.rootStore.emailStore.emails.clear();
    this.rootStore.phoneNumberStore.phoneNumbers.clear();
    this.rootStore.contactStore.contacts.clear();
    this.rootStore.autoReplyStore.autoReplies.clear();
    this.rootStore.autoReplyV2Store.autoRepliesV2.clear();
    this.rootStore.channelStore.channels.clear();
    this.rootStore.errorLogStore.errorLogs.clear();
    this.rootStore.messageStore.messages.clear();
    this.rootStore.settingsStore.settings.clear();
    this.rootStore.cannedMessageStore.cannedMessages.clear();
    this.rootStore.featureFlagStore.featureFlags.clear();
    this.rootStore.workTimeStore.workTimes.clear();

    if (checkForLocation === true && this.locations && this.locations.length > 1) {
      this.setShowLocationPicker(true);
    } else {
      this.setShowLocationPicker(false);
      this.setUsername('');
      this.setPassword('');
      this.setAuthenticationLoading(false);
      this.setLocations([]);
      this.setSelectedLocation(null);
      this.setUserPreviousLoginInfo(null);
    }
  };

  @action setShowMessageLoadScreen = (value) => {
    this.loadingChannelMessages = value;
  };

  @action setToggleSettingsMenu() {
    this.isSettingsMenuOpen = !this.isSettingsMenuOpen;
  }

  @action setSelectedTab = (event, value) => {
    this.selectedTab = value;
  };

  @action setSelectedTabIndex = (index) => {
    this.selectedTab = index;
  };

  @action setChangeLocation(selectedLocation) {
    this.setLogoutStatus(true);
    this.setSelectedLocation(selectedLocation);
    this.setConnectToLocationSocket();
    this.setLoggedIn(true);
  }

  @action setSelectedLocation(selectedLocation) {
    this.selectedLocation = selectedLocation;
  }

  @action setPopoverButton(button = null) {
    this.popoverButton = button;
  }

  @action setToggleRightSideComponent(component, componentName) {
    if (!this.rightSideComponentOpen || this.rightSideComponentOpen.name !== componentName) {
      this.rightSideComponentOpen = component;
    } else {
      this.rightSideComponentOpen = null;
    }
  }

  @action setIncrementLoginRetry() {
    this.loginRetries += 1;
  }

  @action setFullModeChatInput(input) {
    this.fullModeChatInput = input;
  }

  @action setFullModeChatFileUpload(file) {
    this.fullModeChatFileUpload = file;
  }

  @action setSelectedCannedMessage(cannedMessage) {
    this.selectedCannedMessage = cannedMessage;
  }

  @action setMaxVisibleMessages(numberOfMessages) {
    this.maxVisibleMessages = numberOfMessages;
  }

  @action setOldScrollHeight(scrollHeight) {
    this.oldScrollHeight = scrollHeight;
  }

  @action setSpecificScrollHeight(specificHeight) {
    this.specificScrollHeight = specificHeight;
  }

  @action setSearchResultData(resultData) {
    this.searchResultData = resultData;
  }

  @action setLoggedIn(isLoggedIn) {
    this.loggedIn = isLoggedIn;
  }

  @action setShowLocationPicker(showPicker) {
    this.showLocationPicker = showPicker;
  }

  @action setAuthenticationLoading(isLoading) {
    this.authenticationLoading = isLoading;
  }

  @action setLastSeenMessageForChannel(lastSeenMessage) {
    this.lastSeenMessageForChannel = lastSeenMessage;
  }

  @action setIsDataLoaded(isDataLoaded) {
    this.isDataLoaded = isDataLoaded;
  }

  @action setChannelHasMoreMessages(hasMoreMessages) {
    this.channelHasMoreMessages = hasMoreMessages;
  }

  @action setLocationTrackingNumber(locationTrackingNumber) {
    this.locationTrackingNumber = locationTrackingNumber;
  }

  @action setLastNotificationMessage(lastNotificationMessage) {
    this.lastNotificationMessage = lastNotificationMessage;
  }

  @action setDragAndDropChildElementCounter(count) {
    this.dragAndDropChildElementCounter = count;
  }

  @action setIsDisconnected(isDisconnected) {
    this.isDisconnected = isDisconnected;
  }

  @action setReconnectAttemptCount(count) {
    this.reconnectAttemptCount = count;
  }

  @action setCurrentlyFocusedTab(currentlyFocusedTab) {
    this.currentlyFocusedTab = currentlyFocusedTab;
  }

  @action setModalTitle(modalTitle) {
    this.modalTitle = modalTitle;
  }

  @action setNumberOfVisibleTabs(numberOfVisibleTabs) {
    this.numberOfVisibleTabs = numberOfVisibleTabs;
  }

  @action setChannelSearchInput(input) {
    this.channelSearchInput = input;
  }

  @action setIsUploading(isUploading) {
    this.isUploading = isUploading;
  }

  @action setIsLoadingSearchResultData(isLoadingSearchResultData) {
    this.isLoadingSearchResultData = isLoadingSearchResultData;
  }

  @action setMainComponentOpen(component) {
    this.mainComponentOpen = component;
  }

  @action setChannelInfoInnerComponent(component) {
    this.channelInfoInnerComponent = component;
  }

  @action setChatMode(chatMode) {
    this.ChatMode = chatMode;
  }

  @action setUsername(username) {
    this.username = username;
  }

  @action setCpUserId(cpUserId) {
    this.cpUserId = cpUserId;
  }

  @action setPassword(password) {
    this.password = password;
  }

  @action setSnoozeInterval(snoozeInterval) {
    this.snoozeInterval = snoozeInterval;
  }

  @action setIgnoreSettingsEntries(ignoreSettingsEntries) {
    this.ignoreSettingsEntries = ignoreSettingsEntries;
  }

  @action setIgnoreAutoReplyEntries(ignoreAutoReplyEntries) {
    this.ignoreAutoReplyEntries = ignoreAutoReplyEntries;
  }

  @action setIgnoreAutoReplyV2Entries(ignoreAutoReplyEntries) {
    this.ignoreAutoReplyV2Entries = ignoreAutoReplyEntries;
  }

  @action setFaviconBadgeCount(count) {
    utils.setFaviconBadgeCountDebounced(this.favicon, count);
  }

  @action setLocations(locations) {
    this.locations = locations;
  }

  @action setUserPreviousLoginInfo(userPreviousLoginInfo) {
    this.userPreviousLoginInfo = userPreviousLoginInfo;
  }

  @action setLocationHasMoreChannels = (value) => {
    this.locationHasMoreChannels = value;
  };

  @action setIsWaitingForLoadMoreChannels = (value) => {
    this.isWaitingForLoadMoreChannels = value;
  };

  @action setCannedMessagesListOpen = (value) => {
    this.cannedMessagesListOpen = value;
  };

  @action setTransferLocationListOpen = (value) => {
    this.transferLocationListOpen = value;
  };

  @action scrollMessagesToBottom() {
    if (this.messagesContainerRef && this.messagesContainerRef.current) {
      if (this.specificScrollHeight) {
        this.messagesContainerRef.current.scrollTop = this.specificScrollHeight;
        this.setSpecificScrollHeight(0);
      } else if (this.oldScrollHeight === 0) {
        this.messagesContainerRef.current.scrollTop = this.messagesContainerRef.current.scrollHeight;
      } else {
        this.messagesContainerRef.current.scrollTop =
          this.messagesContainerRef.current.scrollHeight - this.oldScrollHeight;
      }
    }
  }

  @action unlockSound = () => {
    if (this.audioContext && this.audioContext.state !== 'suspended') {
      return;
    }

    const events = ['touchstart', 'touchend', 'mousedown', 'keydown'];

    const unlock = () => {
      setTimeout(() => {
        if (!this.audioContext) {
          runInAction(() => {
            this.audioContext = this.AudioContextInstance ? new this.AudioContextInstance() : null;
          });
        }

        this.audioContext
          ?.resume()
          ?.then(() => {
            events.forEach((e) => document.removeEventListener(e, unlock, false));

            if (BrowserDetect.browser === 'Safari') {
              this.notificationsAudioRef
                .play()
                .then()
                .catch(() => {});
              this.notificationsAudioRef.pause();
              this.notificationsAudioRef.currentTime = 0;
            }
          })
          .catch(() => {});
      }, 250);
    };

    events.forEach((e) => document.addEventListener(e, unlock, false));
  };

  @action setCpHeaderHeight = (value) => {
    this.cpHeaderHeight = value;
  };

  @action setIgnoreNotifications(ignoreNotifications) {
    this.ignoreNotifications = ignoreNotifications;
    this.notificationsBlocked = ignoreNotifications;
  }

  @action setIsFetchingSearchResults = (value) => {
    this.isFetchingSearchResults = value;
  };

  @action setCustomModalTitle = (title) => {
    this.customModalTitle = title;
  };

  @action setSnackbarMessage = (text) => {
    this.snackbarMessage = text;
  };

  @action setNotificationData = (text, options, notificationsChannel) => {
    this.notificationsTitle = text;
    this.notificationsOptions = options;
    this.notificationsChannel = notificationsChannel;
  };

  @action setContactInfoMatchedContacts(contacts) {
    this.contactInfoMatchedContacts = contacts;
  }

  @action setToggleDisplaySMSSearchTypes = () => {
    if (
      !this.channelTypeIdsToSearch.includes(utils.ChannelTypeEnum.SMS.TypeId) &&
      !this.contactTypeIdsToSearch.includes(utils.ContactTypeEnum.Customer.TypeId)
    ) {
      this.channelTypeIdsToSearch.push(utils.ChannelTypeEnum.SMS.TypeId);
      this.contactTypeIdsToSearch.push(utils.ContactTypeEnum.Customer.TypeId);
    } else {
      this.channelTypeIdsToSearch = this.channelTypeIdsToSearch.filter(
        (c) => c !== utils.ChannelTypeEnum.SMS.TypeId
      );

      this.contactTypeIdsToSearch = this.contactTypeIdsToSearch.filter(
        (c) => c !== utils.ContactTypeEnum.Customer.TypeId
      );
    }
  };

  @action setToggleDisplayWebchatSearchTypes = (dontUpdateNested = false) => {
    if (
      !this.channelTypeIdsToSearch.includes(utils.ChannelTypeEnum.WebChat.TypeId) &&
      !this.contactTypeIdsToSearch.includes(utils.ContactTypeEnum.WebChatContact.TypeId)
    ) {
      this.channelTypeIdsToSearch.push(utils.ChannelTypeEnum.WebChat.TypeId);
      this.contactTypeIdsToSearch.push(utils.ContactTypeEnum.WebChatContact.TypeId);

      if (!dontUpdateNested) {
        this.showActiveWebChatChannels = true;
        this.showClosedWebChatChannels = true;
      }
    } else {
      this.channelTypeIdsToSearch = this.channelTypeIdsToSearch.filter(
        (c) => c !== utils.ChannelTypeEnum.WebChat.TypeId
      );

      this.contactTypeIdsToSearch = this.contactTypeIdsToSearch.filter(
        (c) => c !== utils.ContactTypeEnum.WebChatContact.TypeId
      );

      if (!dontUpdateNested) {
        this.showActiveWebChatChannels = false;
        this.showClosedWebChatChannels = false;
      }
    }
  };

  @action setToggleDisplayActiveWebchatSearchTypes = () => {
    if (!this.channelTypeIdsToSearch.includes(utils.ChannelTypeEnum.WebChat.TypeId)) {
      this.setToggleDisplayWebchatSearchTypes(true);
    }

    this.showActiveWebChatChannels = !this.showActiveWebChatChannels;
  };

  @action setToggleDisplayClosedWebchatSearchTypes = () => {
    if (!this.channelTypeIdsToSearch.includes(utils.ChannelTypeEnum.WebChat.TypeId)) {
      this.setToggleDisplayWebchatSearchTypes(true);
    }

    this.showClosedWebChatChannels = !this.showClosedWebChatChannels;
  };

  @action setToggleDisplayUnreadChannelFilter = () => {
    this.showUnreadChannelFilter = !this.showUnreadChannelFilter;
  };

  @action setIgnoreLoadMoreMessages = (shouldIgnore) => {
    this.ignoreLoadMoreMessages = shouldIgnore;
  };

  @action reloadChannelsAndMessages = () => {
    this.rootStore.commonStore.setIsDataLoaded(false);
    socketIo.locationSocket.close();
    this.rootStore.channelStore.channels.clear();
    this.rootStore.messageStore.messages.clear();
    this.setIgnoreLoadMoreMessages(true);
    this.setLocationHasMoreChannels(true);
    this.setIsWaitingForLoadMoreChannels(false);
    socketIo.locationSocket.connect();
  };

  @action setIsWebchatEnabled = (enabled) => {
    this.isWebchatEnabled = enabled;
  };

  @action setIsAutoCloseWebChatEnabled = (enabled) => {
    this.isAutoCloseWebChatEnabled = enabled;
  };

  @action loadMoreChannels = () => {
    if (!this.isWaitingForLoadMoreChannels) {
      this.setIsWaitingForLoadMoreChannels(true);
      this.rootStore.sendingEvents.socketEmitLoadMoreChannels(
        this.rootStore.channelStore.getChannels.length,
        utils.maxNumberOfItemsByContainerSize(document, this.leftSideBarListContainerRef, 64),
        this.channelTypeIdsToSearch,
        {
          showActiveWebChatChannels: this.showActiveWebChatChannels,
          showClosedWebChatChannels: this.showClosedWebChatChannels,
          showUnreadChannels: this.showUnreadChannelFilter,
        }
      );
    }
  };

  @action clearChannelNotifications = () => {
    const settings = this.rootStore.settingsStore.getLoggedUserSettings;

    const messagesForChannel = this.rootStore.messageStore.messages.filter(
      (m) =>
        m.Channel &&
        settings?.SelectedChannel &&
        m.Channel._id === settings.SelectedChannel._id &&
        !m.SeenBy.some((s) => s._id === this.getLoggedUser._id)
    );

    if (messagesForChannel && messagesForChannel.length) {
      runInAction(() => {
        messagesForChannel.forEach((message) => {
          message.SeenBy.push(this.getLoggedUser);
        });
      });

      this.setLastSeenMessageForChannel(null);

      this.rootStore.sendingEvents.socketEmitUpdateSeenby(
        messagesForChannel.map((m) => ({
          _id: m._id,
        }))
      );
    }
  };

  @action setClientWebsiteUrl = (value) => {
    this.clientWebsiteUrl = value;
  };

  @action setToggleDisplayEditCustomerNamesForm = (value) => {
    this.displayEditCustomerNamesForm =
      typeof value !== 'undefined' ? value : !this.displayEditCustomerNamesForm;
  };

  @action setToggleIsManageAutoRepliesPopupOpen = () => {
    this.isManageAutoRepliesPopupOpen = !this.isManageAutoRepliesPopupOpen;
  };

  @action setConflictingAutoReplies(conflicting) {
    this.conflicts = conflicting;
  }

  @action setSelectedMessageTypeId(selectedMessageTypeId) {
    this.selectedMessageTypeId = selectedMessageTypeId;
  }

  @action setSelectedAutoReplySwitch(selectedAutoReplySwitch) {
    this.selectedAutoReplySwitch = selectedAutoReplySwitch;
  }

  @action setCustomersData(data) {
    this.customersData = data;
  }

  @action setChatInputStyleHeight = (value) => {
    this.chatInputStyleHeight = value;
  };

  computedGetChannelName = computedFn(function computedGetChannelName(channel) {
    return getChannelName(this.getLoggedUser, channel);
  });
}

export default CommonStore;
